'use client';

import React, { useRef, useEffect, useState } from 'react';

/**
 * Wardley Map Component Data Structure
 */
export interface WardleyComponent {
  name: string;
  visibility: number; // 0-1 (bottom to top on Y axis)
  evolution: number; // 0-1 (left to right on X axis)
  description?: string;
  dependencies?: string[]; // Names of other components this depends on
  inertia?: boolean; // Whether this component has high inertia (resistance to change)
}

export interface EvolutionStage {
  name: string;
  range: [number, number]; // [start, end] along evolution axis
  description: string;
}

export interface Annotation {
  position: [number, number]; // [visibility, evolution]
  text: string;
  type: 'info' | 'warning' | 'opportunity';
}

export interface WardleyMapData {
  title: string;
  components: WardleyComponent[];
  evolution_stages?: EvolutionStage[];
  annotations?: Annotation[];
  xAxisLabel?: string;
  yAxisLabel?: string;
}

interface WardleyMapProps {
  data: WardleyMapData;
  width?: number;
  height?: number;
  interactive?: boolean;
}

/**
 * Default evolution stages (Simon Wardley's framework)
 */
const DEFAULT_EVOLUTION_STAGES: EvolutionStage[] = [
  {
    name: 'Genesis',
    range: [0, 0.25],
    description: 'Novel, uncertain, requires experimentation',
  },
  {
    name: 'Custom Built',
    range: [0.25, 0.50],
    description: 'Understood but not standardized',
  },
  {
    name: 'Product',
    range: [0.50, 0.75],
    description: 'Productized solutions available',
  },
  {
    name: 'Commodity',
    range: [0.75, 1.0],
    description: 'Standardized, utility-like',
  },
];

/**
 * WardleyMap Component
 *
 * Renders an interactive Wardley map visualization showing components,
 * their evolution stage, visibility, and dependencies.
 */
export default function WardleyMap({
  data,
  width = 800,
  height = 600,
  interactive = true,
}: WardleyMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);

  const stages = data.evolution_stages || DEFAULT_EVOLUTION_STAGES;

  // Map coordinates: evolution (0-1) maps to X, visibility (0-1) maps to Y
  const margin = { top: 40, right: 60, bottom: 60, left: 60 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const xScale = (evolution: number) => margin.left + evolution * chartWidth;
  const yScale = (visibility: number) => margin.top + (1 - visibility) * chartHeight;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Set high DPI
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Get colors from CSS variables (theme-aware)
    const computedStyle = getComputedStyle(document.documentElement);
    const isDark = document.documentElement.classList.contains('dark');

    const bgColor = isDark ? '#0A1E2E' : '#F5F5F5'; // Navy or light gray
    const textColor = isDark ? '#E8D5B7' : '#2D3748'; // Cream or dark gray
    const accentColor = '#E89841'; // Orange (same in both themes)
    const gridColor = isDark ? 'rgba(232, 213, 183, 0.2)' : 'rgba(45, 55, 72, 0.2)';
    const stageColor = isDark ? 'rgba(232, 213, 183, 0.03)' : 'rgba(45, 55, 72, 0.03)';

    // Background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw evolution stages (vertical columns)
    stages.forEach((stage) => {
      const x1 = xScale(stage.range[0]);
      const x2 = xScale(stage.range[1]);

      // Stage background
      ctx.fillStyle = stageColor;
      ctx.fillRect(x1, margin.top, x2 - x1, chartHeight);

      // Stage boundary line
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x2, margin.top);
      ctx.lineTo(x2, height - margin.bottom);
      ctx.stroke();

      // Stage label
      ctx.fillStyle = textColor;
      ctx.font = '12px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(stage.name, (x1 + x2) / 2, height - margin.bottom + 20);
    });

    // Draw axes
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 2;

    // X axis (Evolution)
    ctx.beginPath();
    ctx.moveTo(margin.left, height - margin.bottom);
    ctx.lineTo(width - margin.right, height - margin.bottom);
    ctx.stroke();

    // Y axis (Visibility/Value Chain)
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, height - margin.bottom);
    ctx.stroke();

    // Axis labels
    ctx.fillStyle = textColor;
    ctx.font = 'bold 14px Inter, sans-serif';
    ctx.textAlign = 'center';
    const xLabel = data.xAxisLabel || 'Evolution';
    ctx.fillText(`${xLabel} →`, width / 2, height - 10);

    ctx.save();
    ctx.translate(20, height / 2);
    ctx.rotate(-Math.PI / 2);
    const yLabel = data.yAxisLabel || 'Visibility';
    ctx.fillText(`← Low ${yLabel} | High ${yLabel} →`, 0, 0);
    ctx.restore();

    // Draw dependencies first (so they're behind components)
    const dependencyColor = isDark
      ? 'rgba(232, 152, 65, 0.3)'
      : 'rgba(232, 152, 65, 0.5)'; // More visible in light mode

    data.components.forEach((component) => {
      if (component.dependencies) {
        component.dependencies.forEach((depName) => {
          const dep = data.components.find((c) => c.name === depName);
          if (dep) {
            const x1 = xScale(component.evolution);
            const y1 = yScale(component.visibility);
            const x2 = xScale(dep.evolution);
            const y2 = yScale(dep.visibility);

            // Draw arrow from component to dependency
            ctx.strokeStyle = dependencyColor;
            ctx.lineWidth = 1.5;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            ctx.setLineDash([]);

            // Arrow head
            const angle = Math.atan2(y2 - y1, x2 - x1);
            const headLength = 8;
            ctx.fillStyle = dependencyColor;
            ctx.beginPath();
            ctx.moveTo(x2, y2);
            ctx.lineTo(
              x2 - headLength * Math.cos(angle - Math.PI / 6),
              y2 - headLength * Math.sin(angle - Math.PI / 6)
            );
            ctx.lineTo(
              x2 - headLength * Math.cos(angle + Math.PI / 6),
              y2 - headLength * Math.sin(angle + Math.PI / 6)
            );
            ctx.closePath();
            ctx.fill();
          }
        });
      }
    });

    // Draw components
    const componentColor = accentColor; // Orange
    const componentHoverColor = isDark ? '#FFB84D' : '#D17820';
    const inertiaColor = isDark ? '#CC5555' : '#E63946';
    const inertiaHoverColor = isDark ? '#FF6B6B' : '#D62828';
    const highlightColor = isDark ? '#E8D5B7' : '#E89841';
    const labelColor = isDark ? '#E8D5B7' : '#2D3748';

    data.components.forEach((component) => {
      const x = xScale(component.evolution);
      const y = yScale(component.visibility);
      const isHovered = hoveredComponent === component.name;
      const isSelected = selectedComponent === component.name;

      // Component circle
      ctx.beginPath();
      ctx.arc(x, y, isHovered || isSelected ? 10 : 8, 0, 2 * Math.PI);

      if (component.inertia) {
        // Red for high inertia (warning)
        ctx.fillStyle = isHovered || isSelected ? inertiaHoverColor : inertiaColor;
      } else {
        // Orange for normal components
        ctx.fillStyle = isHovered || isSelected ? componentHoverColor : componentColor;
      }
      ctx.fill();

      // Highlight ring for hovered/selected
      if (isHovered || isSelected) {
        ctx.strokeStyle = highlightColor;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Component label
      ctx.fillStyle = labelColor;
      ctx.font = isHovered || isSelected ? 'bold 13px Inter, sans-serif' : '12px Inter, sans-serif';
      ctx.textAlign = x > width / 2 ? 'right' : 'left';
      ctx.fillText(
        component.name,
        x + (x > width / 2 ? -12 : 12),
        y + 4
      );
    });

    // Draw annotations
    if (data.annotations) {
      data.annotations.forEach((annotation) => {
        const x = xScale(annotation.position[1]);
        const y = yScale(annotation.position[0]);

        // Annotation marker colors (same in both themes for consistency)
        const colors = {
          info: '#4A90E2',
          warning: '#FF6B6B',
          opportunity: '#4CAF50',
        };

        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = colors[annotation.type];
        ctx.fill();

        // Annotation text background
        ctx.font = '11px Inter, sans-serif';
        const textWidth = ctx.measureText(annotation.text).width;
        const padding = 6;
        const boxX = x + 10;
        const boxY = y - 10;

        // Theme-aware background
        const annotationBg = isDark ? 'rgba(10, 30, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)';
        ctx.fillStyle = annotationBg;
        ctx.fillRect(boxX - padding, boxY - 12, textWidth + padding * 2, 20);

        // Annotation text
        ctx.fillStyle = colors[annotation.type];
        ctx.textAlign = 'left';
        ctx.fillText(annotation.text, boxX, boxY);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, width, height, hoveredComponent, selectedComponent]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if hovering over any component
    let found = false;
    for (const component of data.components) {
      const cx = xScale(component.evolution);
      const cy = yScale(component.visibility);
      const distance = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);

      if (distance < 12) {
        setHoveredComponent(component.name);
        canvas.style.cursor = 'pointer';
        found = true;
        break;
      }
    }

    if (!found) {
      setHoveredComponent(null);
      canvas.style.cursor = 'default';
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Check if clicking on any component
    for (const component of data.components) {
      const cx = xScale(component.evolution);
      const cy = yScale(component.visibility);
      const distance = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);

      if (distance < 12) {
        setSelectedComponent(selectedComponent === component.name ? null : component.name);
        return;
      }
    }

    setSelectedComponent(null);
  };

  const selectedComponentData = selectedComponent
    ? data.components.find((c) => c.name === selectedComponent)
    : null;

  return (
    <div className="wardley-map-container">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        className="rounded-lg border border-border"
      />

      {/* Component details panel */}
      {selectedComponentData && (
        <div className="mt-4 p-4 bg-background/50 border border-border rounded-lg">
          <h3 className="text-lg font-semibold text-accent mb-2">
            {selectedComponentData.name}
            {selectedComponentData.inertia && (
              <span className="ml-2 text-sm text-red-500">(High Inertia)</span>
            )}
          </h3>
          {selectedComponentData.description && (
            <p className="text-foreground-secondary text-sm mb-2">{selectedComponentData.description}</p>
          )}
          <div className="grid grid-cols-2 gap-2 text-sm text-foreground-tertiary">
            <div>
              <span className="font-medium">Visibility:</span> {(selectedComponentData.visibility * 100).toFixed(0)}%
            </div>
            <div>
              <span className="font-medium">Evolution:</span> {(selectedComponentData.evolution * 100).toFixed(0)}%
            </div>
            <div className="col-span-2">
              <span className="font-medium">Stage:</span>{' '}
              {stages.find(
                (s) =>
                  selectedComponentData.evolution >= s.range[0] &&
                  selectedComponentData.evolution <= s.range[1]
              )?.name || 'Unknown'}
            </div>
            {selectedComponentData.dependencies && selectedComponentData.dependencies.length > 0 && (
              <div className="col-span-2">
                <span className="font-medium">Dependencies:</span>{' '}
                {selectedComponentData.dependencies.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 p-4 bg-background/30 border border-border-muted rounded-lg text-sm">
        <h4 className="font-semibold text-foreground mb-2">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-foreground-secondary">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-accent"></div>
            <span>Component</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500"></div>
            <span>High Inertia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-0.5 bg-accent/30" style={{ borderTop: '1.5px dashed' }}></div>
            <span>Dependency</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span>Annotation</span>
          </div>
        </div>
      </div>
    </div>
  );
}
